/**
 * CENTRAL CONTENT CONFIG
 * ----------------------------------------------------------------------------
 * Every piece of copy, imagery, video, navigation, social link and structured
 * data used across the site lives here. Replace values in this single file to
 * rebrand the entire experience — no component code needs to change.
 *
 * KL-001: Klyne Labs brand + truthful content migration.
 * KL-002A/B: Featured + Selected Work media migrating to owned project assets.
 */

export const brand = {
  name: "KLYNE LABS",
  wordmark: "Klyne Labs",
  tagline: "Built to move business forward.",
  email: "klynelabs@gmail.com",
  phone: "+91 8128706187",
  phoneTel: "+918128706187",
  whatsapp: "https://wa.me/918128706187",
  website: "https://www.klynelabs.in/",
  websiteLabel: "klynelabs.in",
  location: "India-based software and AI studio",
  established: "Software · Web · AI",
  copyright: `© ${new Date().getFullYear()} Klyne Labs. All rights reserved.`,
};

export const nav = {
  links: [
    { label: "Services", href: "#services" },
    { label: "Work", href: "#portfolio" },
    { label: "Process", href: "#process" },
    { label: "About", href: "#about" },
    { label: "Contact", href: "#contact" },
  ],
  cta: { label: "Start a Project", href: "#contact" },
};

export const hero = {
  eyebrow: "SOFTWARE · DIGITAL EXPERIENCES · AI",
  // Each string is one masked line for the signature on-load reveal.
  // KL-001A: rebalanced so each line stays single-line from ~390px through ~1440px
  // (long unbreakable words like "businesses" forced soft-wrap inside masked lines).
  headlineLines: ["We build digital", "systems that", "move business."],
  accentWord: "business",
  subcopy:
    "Premium websites, custom software and intelligent automation—designed around how your business actually works.",
  primaryCta: { label: "Start a Project", href: "#contact" },
  secondaryCta: { label: "Explore Our Work", href: "#portfolio" },
  // Temporary atmospheric backdrop — not a Klyne Labs project asset (KL-002).
  backgroundImage:
    "https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
  // Non-numeric capability labels (no verified company statistics).
  // Short values keep text-3xl readable in three columns at ~390px width.
  stats: [
    { value: "Built", suffix: "", label: "Custom" },
    { value: "Focus", suffix: "", label: "Business" },
    { value: "Scale", suffix: "", label: "End-to-End" },
  ],
};

export const clients = {
  heading: "What we build",
  // Capabilities marquee — not client logos.
  logos: [
    "Animated Websites",
    "Custom Software",
    "AI Automation",
    "SaaS Platforms",
    "CRM Systems",
    "E-Commerce",
    "Voice AI",
    "Business Automation",
  ],
};

export const services = {
  eyebrow: "What we do",
  heading: "Capabilities shaped for ambitious businesses.",
  items: [
    {
      id: "premium-websites",
      index: "01",
      title: "Premium Websites",
      description:
        "Custom marketing and corporate sites with strong visual direction, cinematic motion and clear conversion journeys.",
      icon: "Globe",
      ctaLabel: "Discuss This Service",
      // Temporary placeholder media — not a Klyne Labs project screenshot (KL-002).
      image:
        "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
      imageAlt: "Temporary placeholder — premium website craft workspace",
    },
    {
      id: "custom-software",
      index: "02",
      title: "Custom Software",
      description:
        "Purpose-built web applications, dashboards and operational systems designed around how your business actually works.",
      icon: "MonitorSmartphone",
      ctaLabel: "Discuss This Service",
      image:
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
      imageAlt: "Temporary placeholder — custom software interface",
    },
    {
      id: "e-commerce",
      index: "03",
      title: "E-Commerce",
      description:
        "Storefronts and commerce systems built for product clarity, smooth purchase flows and reliable order operations.",
      icon: "ShoppingBag",
      ctaLabel: "Discuss This Service",
      image:
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
      imageAlt: "Temporary placeholder — commerce and retail interface",
    },
    {
      id: "ai-automation",
      index: "04",
      title: "AI Automation",
      description:
        "AI-assisted workflows, intelligent routing and integrations that reduce repetitive work and improve responsiveness.",
      icon: "Bot",
      ctaLabel: "Discuss This Service",
      image:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
      imageAlt: "Temporary placeholder — AI systems visualization",
    },
    {
      id: "crm-business-systems",
      index: "05",
      title: "CRM & Business Systems",
      description:
        "Secure systems for managing customers, teams, tasks, content and enquiries as one connected operational layer.",
      icon: "LayoutDashboard",
      ctaLabel: "Discuss This Service",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
      imageAlt: "Temporary placeholder — business systems dashboard",
    },
    {
      id: "ai-voice-communication",
      index: "06",
      title: "AI Voice & Communication",
      description:
        "Voice, appointment and front-desk systems that handle conversations, collect information and route the next step.",
      icon: "Mic",
      ctaLabel: "Discuss This Service",
      image:
        "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
      imageAlt: "Temporary placeholder — communication systems",
    },
  ],
};

export const whyChooseUs = {
  eyebrow: "Why Klyne Labs",
  heading: "We build around the business, not around the tool.",
  reasons: [
    {
      title: "Business Before Technology",
      body: "We start with the business problem, users and constraints—then define the system that actually serves them.",
    },
    {
      title: "Built Specifically for You",
      body: "Custom solutions shaped to your workflow, not recycled templates forced onto your operations.",
    },
    {
      title: "Design and Engineering Together",
      body: "Visual craft and technical delivery develop as one process, so the final product feels coherent and dependable.",
    },
    {
      title: "Built for Long-Term Growth",
      body: "Systems designed to launch clearly and evolve as priorities, teams and customers change.",
    },
  ],
};

export const process = {
  eyebrow: "The method",
  heading: "Five stages, one clear outcome.",
  chapters: [
    {
      no: "01",
      title: "Discover",
      body: "Understand the business, users, workflow, constraints and the outcome that matters.",
      // Temporary placeholder media (KL-002).
      image:
        "https://images.unsplash.com/photo-1517842645767-c639042777db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    },
    {
      no: "02",
      title: "Architect",
      body: "Define the right feature set, content structure, technology and delivery plan.",
      image:
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    },
    {
      no: "03",
      title: "Design",
      body: "Create the visual system, interface and interaction experience with purpose.",
      image:
        "https://images.unsplash.com/photo-1561070791-2526d30994b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    },
    {
      no: "04",
      title: "Build",
      body: "Develop, integrate, test and prepare the complete product for real use.",
      image:
        "https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    },
    {
      no: "05",
      title: "Launch & Improve",
      body: "Deploy, monitor and refine based on real usage and the next priorities for growth.",
      image:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    },
  ],
};

export const featured = {
  eyebrow: "Featured work",
  heading: "Selected builds in focus.",
  /**
   * Reusable media model. Each reel provides a `poster` (always shown first /
   * as fallback) and an ordered `sources` array. Each source has a `src` and a
   * `type`, and an optional `kind` describing where it lives:
   *   kind: "local"    -> self-hosted file in /public (default, most reliable)
   *   kind: "cdn"      -> a CDN URL (e.g. Mixkit, Coverr, Cloudinary, Mux, S3)
   *   kind: "external" -> any third-party URL (may be blocked by hotlink rules)
   * The <Media> component lazy-loads, autoplays muted+looping, and gracefully
   * falls back to the poster if no source can play.
   *
   * KL-002A: Featured reels use owned screen recordings (local WebM + MP4).
   */
  reels: [
    {
      id: "velora",
      title: "Velora",
      category: "Animated Marketing Agency Website",
      year: "Portfolio",
      poster: "/media/projects/velora/poster.jpg",
      sources: [
        {
          src: "/media/projects/velora/video.webm",
          type: "video/webm",
          kind: "local",
        },
        {
          src: "/media/projects/velora/video.mp4",
          type: "video/mp4",
          kind: "local",
        },
      ],
    },
    {
      id: "real-estate-walkthrough",
      title: "Cinematic Real Estate Walkthrough",
      category: "Immersive Property Website",
      year: "Portfolio",
      poster: "/media/projects/real-estate-walkthrough/poster.jpg",
      sources: [
        {
          src: "/media/projects/real-estate-walkthrough/video.webm",
          type: "video/webm",
          kind: "local",
        },
        {
          src: "/media/projects/real-estate-walkthrough/video.mp4",
          type: "video/mp4",
          kind: "local",
        },
      ],
    },
  ],
};

export const portfolio = {
  eyebrow: "Selected work",
  heading: "Real projects. Clear purpose.",
  /**
   * KL-002B: Selected Work lineup is ViaVia, The Iconic Stones, AiraDesk.
   * Velora and Home No. 134 remain in Featured only (KL-002A).
   */
  projects: [
    {
      id: "viavia",
      slug: "viavia",
      title: "ViaVia",
      category: "Real-estate matching mobile application",
      description:
        "A real-estate matching platform where buyer property requirements are matched with relevant seller property listings.",
      year: "Published",
      href: "https://apps.apple.com/in/app/viavia-network/id6771621059",
      actionLabel: "View on App Store",
      // KL-002B: owned ViaVia reference image (portrait App Store / product visual).
      image: "/media/projects/viavia/cover.jpg",
      size: "tall",
    },
    {
      id: "the-iconic-stones",
      slug: "the-iconic-stones",
      title: "The Iconic Stones",
      category: "Marble company website",
      description:
        "A premium website for a marble and natural-stone company, focused on visual presentation, materials and enquiry generation.",
      year: "Live",
      href: "https://www.theiconicstones.com/",
      actionLabel: "Visit Website",
      // KL-002B: truthful snapshot from theiconicstones.com homepage.
      image: "/media/projects/iconic-stones/cover.jpg",
      size: "wide",
    },
    {
      id: "airadesk",
      slug: "airadesk-hospital-receptionist",
      title: "AiraDesk Hospital Receptionist",
      category: "AI voice and hospital communication system",
      description:
        "An AI hospital receptionist that handles appointments, general hospital and doctor queries, insurance-related queries and emergency human handoff.",
      year: "Product",
      statusLabel: "Product Showcase",
      // TEMPORARY PLACEHOLDER IMAGE — not AiraDesk UI; no approved product screenshot yet (KL-002B).
      image:
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
      size: "wide",
    },
  ],
};

export const about = {
  eyebrow: "About Klyne Labs",
  statement:
    "Klyne Labs is a software and AI studio focused on building digital products that are both visually distinctive and operationally useful.",
  body: [
    "We work across premium websites, custom software and intelligent automation—combining strategy, design and engineering under one roof.",
    "Klyne Labs exists to build digital systems that businesses can depend on. We understand the business first, define the right system, then bring design and engineering together in one focused build process.",
  ],
  // Official full Klyne Labs logo (KL-002C). Capability thumbs remain temporary.
  portrait: "/media/brand/klyne-labs-logo.png",
  // Capability areas — not people. Photos are temporary placeholders.
  team: [
    {
      name: "Web & Experience",
      role: "Animated websites · product interfaces",
      image:
        "https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
    {
      name: "Software & AI",
      role: "Platforms · automation · voice systems",
      image:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    },
  ],
};

export const testimonials = {
  eyebrow: "How We Think",
  heading: "Principles that guide the work.",
  // Studio principles — not customer testimonials.
  quotes: [
    {
      quote: "Every feature should solve a real business problem.",
      name: "Principle",
      title: "Business Before Technology",
    },
    {
      quote: "Powerful software should still feel simple to use.",
      name: "Principle",
      title: "Clarity Over Complexity",
    },
    {
      quote: "A digital product should be built for growth, not only for launch.",
      name: "Principle",
      title: "Built to Evolve",
    },
    {
      quote: "Design and engineering should work as one system.",
      name: "Principle",
      title: "Crafted Together",
    },
  ],
};

export const faq = {
  eyebrow: "Questions",
  heading: "Everything you might ask.",
  items: [
    {
      q: "What does Klyne Labs build?",
      a: "We build premium websites, custom software, SaaS products, business platforms and AI-powered communication or automation systems.",
    },
    {
      q: "Can you improve an existing website or software product?",
      a: "Yes. We can audit an existing product, identify what should be preserved, and improve its design, performance, structure or functionality in controlled phases.",
    },
    {
      q: "Do you provide both design and development?",
      a: "Yes. Strategy, interface design, frontend development, backend development and integration planning can be handled as one connected process.",
    },
    {
      q: "Do you build AI systems?",
      a: "Yes. Our work can include conversational AI, voice systems, workflow automation, intelligent routing and integrations with existing business tools.",
    },
    {
      q: "How long does a project take?",
      a: "Timelines depend on the scope, content, integrations and approval cycle. A focused website may take several weeks, while a custom platform is planned in phases.",
    },
    {
      q: "How is pricing determined?",
      a: "Pricing is based on the project scope, complexity, design depth, integrations and delivery requirements. We define the scope before providing a formal estimate.",
    },
    {
      q: "Will the client own the final product?",
      a: "Ownership and licensing are defined clearly in the project agreement. Client-specific deliverables can be transferred according to the agreed terms.",
    },
    {
      q: "Do you provide support after launch?",
      a: "Ongoing support, maintenance and improvement plans can be included based on the needs of the project.",
    },
    {
      q: "How do we start?",
      a: "Share what you are trying to build, the problem it should solve and any existing material. Klyne Labs will recommend the most practical next step.",
    },
  ],
};

export const contact = {
  eyebrow: "Start a project",
  heading: "Have something ambitious in mind?",
  subcopy:
    "Tell us what you want to build, improve or automate. We’ll help you determine the right next step.",
  services: [
    "Premium Websites",
    "Custom Software",
    "E-Commerce",
    "AI Automation",
    "CRM & Business Systems",
    "AI Voice & Communication",
    "Something else",
  ],
  budgets: ["Exploratory", "Focused build", "Platform / multi-phase", "Let’s discuss"],
  ctaLabel: "Tell Us What You’re Building",
};

export const footer = {
  bigWord: "KLYNE LABS",
  invitation: "Have something ambitious in mind?",
  columns: [
    {
      title: "Studio",
      links: [
        { label: "Services", href: "#services" },
        { label: "Work", href: "#portfolio" },
        { label: "About", href: "#about" },
        { label: "Process", href: "#process" },
      ],
    },
    {
      title: "Connect",
      links: [
        { label: "Email", href: "mailto:klynelabs@gmail.com" },
        { label: "Phone", href: "tel:+918128706187" },
        { label: "WhatsApp", href: "https://wa.me/918128706187" },
        { label: "Website", href: "https://www.klynelabs.in/" },
      ],
    },
  ],
  // Social profile URLs are pending — do not link to bare network homepages.
  social: [],
};

const siteContent = {
  brand,
  nav,
  hero,
  clients,
  services,
  whyChooseUs,
  process,
  featured,
  portfolio,
  about,
  testimonials,
  faq,
  contact,
  footer,
};

export default siteContent;
